import { z } from "zod";

// Italian Fiscal Code regex (16 alphanumeric characters)
const fiscalCodeRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i;

// Italian VAT number regex (11 digits)
const vatNumberRegex = /^[0-9]{11}$/;

// Italian phone regex (allows various formats)
const phoneRegex = /^(\+39)?[\s]?[0-9]{6,12}$/;

// License plate regex (Italian format)
const licensePlateRegex = /^[A-Z]{2}[0-9]{3}[A-Z]{2}$/i;

// Custom validation functions
const isValidFiscalCode = (code) => {
  if (!code) return true; // Optional field
  return fiscalCodeRegex.test(code.replace(/\s/g, ''));
};

const isValidVatNumber = (vat) => {
  if (!vat) return true; // Optional field
  return vatNumberRegex.test(vat.replace(/\s/g, ''));
};

const isValidPhone = (phone) => {
  if (!phone) return true; // Optional field
  return phoneRegex.test(phone.replace(/[\s\-\.]/g, ''));
};

const isValidLicensePlate = (plate) => {
  if (!plate) return true; // Optional field
  return licensePlateRegex.test(plate.replace(/[\s\-]/g, ''));
};

// Client Schema
export const clientSchema = z.object({
  name: z.string()
    .min(2, "Il nome deve avere almeno 2 caratteri")
    .max(100, "Il nome non può superare i 100 caratteri"),
  
  client_type: z.enum(["individual", "company"], {
    errorMap: () => ({ message: "Seleziona il tipo di cliente" })
  }),
  
  fiscal_code: z.string()
    .optional()
    .refine((val) => !val || isValidFiscalCode(val), {
      message: "Codice fiscale non valido (16 caratteri alfanumerici)"
    }),
  
  vat_number: z.string()
    .optional()
    .refine((val) => !val || isValidVatNumber(val), {
      message: "Partita IVA non valida (11 cifre)"
    }),
  
  email: z.string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: "Email non valida"
    }),
  
  phone: z.string()
    .optional()
    .refine((val) => !val || isValidPhone(val), {
      message: "Numero di telefono non valido"
    }),
  
  address: z.string()
    .max(200, "L'indirizzo non può superare i 200 caratteri")
    .optional(),
  
  city: z.string()
    .max(100, "La città non può superare i 100 caratteri")
    .optional(),
  
  province: z.string()
    .max(2, "La provincia deve essere di 2 caratteri (es. MI)")
    .optional(),
  
  postal_code: z.string()
    .max(5, "Il CAP deve essere di 5 cifre")
    .optional()
    .refine((val) => !val || /^[0-9]{5}$/.test(val), {
      message: "CAP non valido (5 cifre)"
    }),
  
  birth_date: z.string().optional(),
  
  notes: z.string()
    .max(1000, "Le note non possono superare i 1000 caratteri")
    .optional()
}).refine((data) => {
  // If company, VAT is recommended
  // If individual, fiscal code is recommended
  return true; // Just a soft validation for now
}, {
  message: "Verifica i dati inseriti"
});

// Policy Schema
export const policySchema = z.object({
  policy_number: z.string()
    .min(1, "Il numero polizza è obbligatorio")
    .max(50, "Il numero polizza non può superare i 50 caratteri"),
  
  client_id: z.string()
    .min(1, "Seleziona un cliente"),
  
  company: z.string()
    .min(1, "Seleziona una compagnia")
    .max(100, "Il nome compagnia non può superare i 100 caratteri"),
  
  policy_type: z.string()
    .min(1, "Seleziona il tipo di polizza"),
  
  payment_frequency: z.enum(["annual", "semiannual"], {
    errorMap: () => ({ message: "Seleziona la frequenza di pagamento" })
  }).default("annual"),
  
  premium: z.number()
    .min(0, "Il premio non può essere negativo")
    .max(1000000, "Il premio sembra troppo alto"),
  
  consultation_fee: z.number()
    .min(0, "La consulenza non può essere negativa")
    .max(100000, "La consulenza sembra troppo alta")
    .optional()
    .default(0),
  
  start_date: z.string()
    .min(1, "La data di inizio è obbligatoria")
    .refine((val) => !isNaN(Date.parse(val)), {
      message: "Data di inizio non valida"
    }),
  
  expiry_date: z.string()
    .min(1, "La data di scadenza è obbligatoria")
    .refine((val) => !isNaN(Date.parse(val)), {
      message: "Data di scadenza non valida"
    }),
  
  semiannual_expiry: z.string().optional(),
  
  license_plate: z.string()
    .optional()
    .refine((val) => !val || isValidLicensePlate(val), {
      message: "Targa non valida (formato: XX000XX)"
    }),
  
  notes: z.string()
    .max(2000, "Le note non possono superare i 2000 caratteri")
    .optional()
}).refine((data) => {
  // Validate that expiry_date is after start_date
  if (data.start_date && data.expiry_date) {
    return new Date(data.expiry_date) > new Date(data.start_date);
  }
  return true;
}, {
  message: "La data di scadenza deve essere successiva alla data di inizio",
  path: ["expiry_date"]
}).refine((data) => {
  // If semiannual, semiannual_expiry should be set
  if (data.payment_frequency === "semiannual" && !data.semiannual_expiry) {
    return false;
  }
  return true;
}, {
  message: "Per polizze semestrali, inserisci la data della rata semestrale",
  path: ["semiannual_expiry"]
});

// Email settings schema
export const emailSettingsSchema = z.object({
  email: z.string()
    .email("Email non valida"),
  
  app_password: z.string()
    .min(16, "La password app deve avere almeno 16 caratteri"),
  
  imap_server: z.string()
    .min(1, "Server IMAP obbligatorio")
    .default("imap.gmail.com"),
  
  imap_port: z.number()
    .min(1, "Porta non valida")
    .max(65535, "Porta non valida")
    .default(993),
  
  check_interval: z.number()
    .min(1, "Intervallo minimo 1 minuto")
    .max(60, "Intervallo massimo 60 minuti")
    .default(5),
  
  auto_delete_days: z.number()
    .min(0, "Giorni non validi")
    .max(365, "Massimo 365 giorni")
    .default(30)
});

// Helper function to format validation errors for display
export const formatValidationErrors = (errors) => {
  return Object.entries(errors).map(([field, error]) => ({
    field,
    message: error.message || "Campo non valido"
  }));
};

// Export individual validators for inline use
export const validators = {
  fiscalCode: isValidFiscalCode,
  vatNumber: isValidVatNumber,
  phone: isValidPhone,
  licensePlate: isValidLicensePlate,
  email: (val) => !val || z.string().email().safeParse(val).success,
  postalCode: (val) => !val || /^[0-9]{5}$/.test(val),
  province: (val) => !val || /^[A-Z]{2}$/i.test(val)
};

export default {
  clientSchema,
  policySchema,
  emailSettingsSchema,
  validators,
  formatValidationErrors
};
